import { EditableContent } from '../../edit/EditableContent';
import { EditableMedia } from '../../edit/EditableMedia';
import { buildSiteImageProxyUrl, buildSiteImageSrcSet } from '../../lib/imageProxy';
import { SITE_IMAGE_SIZES } from '../../lib/responsiveImageSizes';
import { pickBlockField } from '../../lib/localized';
import { pickMediaUrl } from '../../lib/media';
import { splitTastingCourseLabel } from '../../lib/tastingCourseLabel';
import type { BlockRenderProps } from '../../types';

function parseCourses(raw: string): string[] {
  return raw
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);
}

export function TastingMenuCourseTimeline({
  block,
  locale,
  isActive,
  activeEditTarget,
  editable,
}: BlockRenderProps) {
  const heading = pickBlockField(block.content, 'heading', locale);
  const supporting = pickBlockField(block.content, 'supporting', locale);
  const coursesRaw = pickBlockField(block.content, 'courses', locale);
  const courses = parseCourses(coursesRaw);
  const pairingWine = pickBlockField(block.content, 'pairingWine', locale);
  const pairingZero = pickBlockField(block.content, 'pairingZero', locale);
  const target = isActive ? activeEditTarget : null;
  const showPairings = Boolean(editable || pairingWine.trim() || pairingZero.trim());
  const hasCourseMedia = courses.some((_, index) =>
    Boolean(pickMediaUrl(block, `courseImage${index}`)),
  );

  return (
    <section
      className={[
        'ws-tasting',
        hasCourseMedia ? 'ws-tasting--course-media' : '',
        isActive ? 'ws-block--active' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      data-block-id={block.blockId}
      data-component={block.componentType}
      id="tasting"
    >
      <div className="ws-tasting__inner">
        <EditableContent
          as="h2"
          fieldKey="heading"
          editable={editable}
          activeEditTarget={target}
          className="ws-tasting__title"
        >
          {heading}
        </EditableContent>
        <EditableContent
          fieldKey="supporting"
          editable={editable}
          activeEditTarget={target}
          className="ws-tasting__subtitle"
        >
          {supporting}
        </EditableContent>
        <ol className="ws-tasting__timeline">
          {courses.map((course, index) => {
            const mediaKey = `courseImage${index}`;
            const courseImage = pickMediaUrl(block, mediaKey);
            const courseSrc = courseImage
              ? buildSiteImageProxyUrl(courseImage, 800, 78)
              : undefined;
            const courseSrcSet = courseImage
              ? buildSiteImageSrcSet(courseImage, [400, 640, 800, 1200], 78)
              : undefined;
            const { step, name, note } = splitTastingCourseLabel(course);
            const stepLabel = step || String(index + 1).padStart(2, '0');
            return (
              <li key={`${course}-${index}`} className="ws-tasting__course">
                {courseSrc ? (
                  <div className="ws-tasting__course-media">
                    <EditableMedia
                      fieldKey={mediaKey}
                      editable={editable}
                      activeEditTarget={target}
                      className="ws-tasting__course-img"
                      src={courseSrc}
                      srcSet={courseSrcSet}
                      sizes={SITE_IMAGE_SIZES.menuDishCard}
                      alt={name || `Course ${index + 1}`}
                      width={800}
                      height={600}
                      loading={index === 0 ? 'eager' : 'lazy'}
                      decoding="async"
                    />
                  </div>
                ) : null}
                <span className="ws-tasting__course-index">{stepLabel}</span>
                {hasCourseMedia ? (
                  <div className="ws-tasting__course-body">
                    <span className="ws-tasting__course-label">{name}</span>
                    {note ? <span className="ws-tasting__course-note">{note}</span> : null}
                  </div>
                ) : (
                  <span className="ws-tasting__course-label">{course}</span>
                )}
              </li>
            );
          })}
        </ol>
        {showPairings ? (
          <div className="ws-tasting__pairings">
            <EditableContent
              fieldKey="pairingWine"
              editable={editable}
              activeEditTarget={target}
              className="ws-tasting__pairing"
            >
              {pairingWine}
            </EditableContent>
            <EditableContent
              fieldKey="pairingZero"
              editable={editable}
              activeEditTarget={target}
              className="ws-tasting__pairing"
            >
              {pairingZero}
            </EditableContent>
          </div>
        ) : null}
      </div>
    </section>
  );
}
